import { Injectable } from '@angular/core';
import { Observable, from, map, switchMap } from 'rxjs';
import { ApiService } from './api.service';

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['.png', '.jpg', '.jpeg'];
const ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg'];

interface MediaSystemParams {
  customerBk: string;
  systemBk: string;
}

export interface ImageUploadSelectionResult {
  validationError: string | null;
  pendingUploadKey: string | null;
}

export interface ImageUploadUrlRequest {
  fileName: string;
  contentType: string;
  fileSize: number;
}

export interface ImageUploadUrlResponse {
  uploadUrl: string;
  imageKey: string;
}

const PENDING_UPLOAD_KEY = '__pending_upload__';

@Injectable({
  providedIn: 'root',
})
export class ImageUploadService {
  readonly accept = ALLOWED_EXTENSIONS.join(',');
  private file: File | null = null;
  private previewObjectUrl: string | null = null;

  constructor(private baseApiService: ApiService) {}

  validateImageFile(file: File): string | null {
    const fileName = file.name.toLowerCase();
    const hasAllowedExtension = ALLOWED_EXTENSIONS.some((extension) => fileName.endsWith(extension));

    if (!hasAllowedExtension) {
      return 'Unsupported image format. Use .png, .jpeg or .jpg.';
    }
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      return 'Unsupported MIME type. Only image/png and image/jpeg are allowed.';
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return 'Image file size must be 5MB or less.';
    }

    return null;
  }

  selectFile(file: File): ImageUploadSelectionResult {
    const validationError = this.validateImageFile(file);
    if (validationError) {
      this.clear();
      return {
        validationError,
        pendingUploadKey: null,
      };
    }

    this.file = file;
    this.setPreviewObjectUrl(URL.createObjectURL(file));
    return {
      validationError: null,
      pendingUploadKey: PENDING_UPLOAD_KEY,
    };
  }

  getFile(): File | null {
    return this.file;
  }

  getPreviewObjectUrl(): string | null {
    return this.previewObjectUrl;
  }

  clear(): void {
    this.file = null;
    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
      this.previewObjectUrl = null;
    }
  }

  uploadWithPresignedUrl(
    file: File,
    requestUploadUrl: (payload: ImageUploadUrlRequest) => Observable<ImageUploadUrlResponse>
  ): Observable<string> {
    return requestUploadUrl({
      fileName: file.name,
      contentType: file.type,
      fileSize: file.size,
    }).pipe(
      switchMap(({ uploadUrl, imageKey }) =>
        from(
          fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': file.type },
            body: file,
          })
        ).pipe(
          map((response) => {
            if (!response.ok) {
              throw new Error(`Image upload failed with status ${response.status}`);
            }
            return imageKey;
          })
        )
      )
    );
  }

  requestImageUploadUrl(
    params: MediaSystemParams,
    payload: ImageUploadUrlRequest,
    uploadScope: string = 'widget-images'
  ): Observable<ImageUploadUrlResponse> {
    return this.baseApiService.createData<null, ImageUploadUrlRequest, ImageUploadUrlResponse>(
      `${this.getMediaBasePath(params)}/images/upload-url/${uploadScope}`,
      undefined,
      payload
    );
  }

  private getMediaBasePath({ customerBk, systemBk }: MediaSystemParams): string {
    return `analytics/media/customers/${customerBk}/systems/${systemBk}`;
  }

  private setPreviewObjectUrl(url: string): void {
    if (this.previewObjectUrl) {
      URL.revokeObjectURL(this.previewObjectUrl);
    }
    this.previewObjectUrl = url;
  }
}
